<template>
  <div v-if="isVisible" class="refund-box">
    <!-- Sezione creazione rimborso -->
    <div class="refund-section" v-if="canCreateRefund">
      <div class="refund-section__header">
        <CIcon name="cil-money" class="mr-2"/>
        <translate translate-context="accounting.refund">Rimborso</translate>
        <CBadge v-if="hasRefunds" color="danger" class="ml-2">
          {{ refundedPercentage }}%
          <translate translate-context="accounting.refund">rimborsato</translate>
        </CBadge>
      </div>

      <div class="mt-3">
        <!-- Riepilogo importi -->
        <CRow class="mb-3">
          <CCol md="4">
            <small class="text-muted d-block">
              <translate translate-context="accounting.refund">Importo rimborsabile</translate>
            </small>
            <strong>{{ formatAmount(refundSummary.total_refundable) }} EUR</strong>
          </CCol>
          <CCol md="4">
            <small class="text-muted d-block">
              <translate translate-context="accounting.refund">Già rimborsato</translate>
            </small>
            <span :class="{ 'text-danger': refundSummary.total_refunded > 0 }">
              {{ formatAmount(refundSummary.total_refunded) }} EUR
            </span>
          </CCol>
          <CCol md="4">
            <small class="text-muted d-block">
              <translate translate-context="accounting.refund">Disponibile</translate>
            </small>
            <strong class="text-success">{{ formatAmount(refundSummary.available) }} EUR</strong>
          </CCol>
        </CRow>

        <!-- Photo Selections Preview -->
        <RefundPhotosPreview
          v-if="hasPhotoSelections"
          :removed-photos="removedPhotos"
          class="mb-3"
          @clear-removed="$emit('clear-photos', 'removed')"
        />

        <!-- Input importo + percentuale -->
        <div class="refund-input-row">
          <div class="refund-input-group">
            <label class="small text-muted mb-1">
              <translate translate-context="accounting.refund">Importo da rimborsare</translate>
            </label>
            <div class="d-flex align-items-center">
              <CInput
                v-model="refundAmount"
                type="number"
                step="0.01"
                min="0"
                :max="refundSummary.available"
                :placeholder="$pgettext('accounting.refund', '0.00')"
                class="refund-amount-input"
                :class="{ 'is-invalid': amountError }"
                :disabled="calculatingRefund"
              />
              <span class="text-muted ml-2">EUR</span>
              <CButton
                v-if="showRecalculateButton"
                color="info"
                variant="outline"
                size="sm"
                class="ml-2"
                :disabled="calculatingRefund"
                @click="calculateRefund"
              >
                <CSpinner v-if="calculatingRefund" size="sm" class="mr-1"/>
                <CIcon v-else name="cil-reload" size="sm" class="mr-1"/>
                <translate translate-context="accounting.refund">Ricalcola</translate>
              </CButton>
            </div>
            <small v-if="calculatingRefund" class="text-info">
              <CSpinner size="sm" class="mr-1"/>
              <translate translate-context="accounting.refund">Calcolo importo in corso...</translate>
            </small>
            <small v-else-if="amountError" class="text-danger">
              <translate translate-context="accounting.refund">L'importo supera il disponibile</translate>
            </small>
          </div>

          <div v-if="refundAmountNum > 0" class="refund-percentage-badge ml-3">
            <CBadge :color="percentageColor" class="refund-pct">
              {{ currentPercentage }}%
            </CBadge>
          </div>
        </div>

        <!-- Tipo e motivo -->
        <CRow class="mt-3">
          <CCol md="4">
            <label class="small text-muted mb-1">
              <translate translate-context="accounting.refund">Tipo rimborso</translate>
            </label>
            <CSelect
              :value.sync="selectedRefundType"
              :options="refundTypeOptions"
              size="sm"
            />
          </CCol>
          <CCol md="8">
            <label class="small text-muted mb-1">
              <translate translate-context="accounting.refund">Motivo</translate>
            </label>
            <CInput
              v-model="refundReason"
              size="sm"
              :placeholder="$pgettext('accounting.refund', 'Inserisci il motivo del rimborso...')"
              maxlength="500"
            />
          </CCol>
        </CRow>

        <!-- Azioni -->
        <div class="refund-actions mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              @click="fillMax"
              class="mr-2"
            >
              <CIcon name="cil-arrow-thick-from-bottom" size="sm" class="mr-1"/>
              <translate translate-context="accounting.refund">Rimborso completo</translate>
            </CButton>
            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              @click="clearAll"
              :disabled="refundAmountNum === 0 && !hasPhotoSelections"
            >
              <CIcon name="cil-x" size="sm" class="mr-1"/>
              <translate translate-context="accounting.refund">Annulla</translate>
            </CButton>
          </div>
          <CButton
            color="warning"
            size="sm"
            :disabled="!canSubmit"
            @click="openConfirmModal"
          >
            <CIcon name="cil-check" size="sm" class="mr-1"/>
            <translate translate-context="accounting.refund">Conferma rimborso</translate>
          </CButton>
        </div>
      </div>
    </div>

    <!-- No refundable items -->
    <div v-else-if="!hasRefunds" class="refund-section">
      <div class="text-muted text-center py-3">
        <translate translate-context="accounting.refund">Nessun articolo rimborsabile</translate>
      </div>
    </div>

    <!-- Storico rimborsi -->
    <div v-if="hasRefunds" class="refund-section">
      <div class="refund-section__header">
        <CIcon name="cil-history" class="mr-2"/>
        <translate translate-context="accounting.refund">Rimborsi precedenti</translate>
      </div>
      <div class="mt-3">
        <div
          v-for="refund in refundsList"
          :key="refund.id"
          class="history-item"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <router-link :to="getRefundRoute(refund.id)" class="reference-link mr-2">
                #{{ refund.id }}
              </router-link>
              <span class="text-muted small mr-2">{{ formatDate(refund.created_at) }}</span>
              <CBadge color="danger">{{ formatAmount(refund.amount) }} EUR</CBadge>
              <span v-if="refund.refund_type" class="text-muted small ml-2">
                {{ getRefundTypeLabel(refund.refund_type) }}
              </span>
            </div>
            <router-link :to="getRefundRoute(refund.id)" class="text-muted">
              <CIcon name="cil-external-link" size="sm"/>
            </router-link>
          </div>
          <div v-if="refund.reason" class="text-muted small mt-1 font-italic">
            {{ refund.reason }}
          </div>
        </div>
      </div>
    </div>

    <!-- Modale conferma -->
    <CModal
      :show.sync="showConfirmModal"
      centered
      :title="$pgettext('accounting.refund', 'Conferma rimborso')"
    >
      <div class="confirm-content">
        <div class="confirm-row">
          <span class="text-muted">
            <translate translate-context="accounting.refund">Articolo</translate>
          </span>
          <strong>#{{ orderItem.id }} — {{ orderItem.pica_service_name }}</strong>
        </div>
        <div class="confirm-row">
          <span class="text-muted">
            <translate translate-context="accounting.refund">Importo rimborso</translate>
          </span>
          <strong class="text-danger">{{ formatAmount(refundAmountNum) }} EUR</strong>
        </div>
        <div class="confirm-row">
          <span class="text-muted">
            <translate translate-context="accounting.refund">Percentuale</translate>
          </span>
          <CBadge :color="percentageColor">{{ currentPercentage }}%</CBadge>
        </div>
        <div class="confirm-row">
          <span class="text-muted">
            <translate translate-context="accounting.refund">Tipo</translate>
          </span>
          <span>{{ getSelectedRefundTypeName() }}</span>
        </div>
        <div class="confirm-row">
          <span class="text-muted">
            <translate translate-context="accounting.refund">Motivo</translate>
          </span>
          <span>{{ refundReason }}</span>
        </div>
        <div v-if="hasPhotoSelections" class="confirm-row">
          <span class="text-muted">
            <translate translate-context="accounting.refund">Foto</translate>
          </span>
          <span>
            <span v-if="removedPhotos.length > 0" class="text-danger">
              <CIcon name="cil-trash" size="sm"/>
              {{ removedPhotos.length }} rimosse
            </span>
          </span>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" @click="showConfirmModal = false">
          <translate translate-context="accounting.refund">Annulla</translate>
        </CButton>
        <CButton
          color="warning"
          @click="submitRefund"
          :disabled="creatingRefund"
        >
          <span v-if="creatingRefund">
            <CSpinner size="sm" class="mr-1"/>
            <translate translate-context="accounting.refund">Creazione...</translate>
          </span>
          <span v-else>
            <translate translate-context="accounting.refund">Crea rimborso</translate>
          </span>
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import RefundPhotosPreview from './RefundPhotosPreview.vue'
import AccountingService from '../../service'

export default {
  name: 'RefundBox',
  components: {
    RefundPhotosPreview
  },
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    scope: {
      type: Object,
      default: () => ({ type: 'global', id: null })
    },
    removedPhotos: {
      type: Array,
      default: () => []
    },
    storeNamespace: {
      type: String,
      default: 'accounting/order'
    },
    purchasedCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      refundAmount: '',
      showConfirmModal: false,
      refundReason: '',
      selectedRefundType: null,
      calculatingRefund: false,
      calculatedFromPhotos: false
    }
  },
  computed: {
    ...mapGetters('core', ['refundTypes']),

    // Dynamic store namespace: uses storeNamespace prop to support both
    // accounting/order and accounting/movement stores (Vue 2 mapGetters doesn't support dynamic namespaces)
    creatingRefund() {
      return this.$store.getters[`${this.storeNamespace}/creatingRefund`]
    },

    isVisible() {
      return this.orderItem.refunds !== null
    },

    refundsList() {
      return this.orderItem.refunds || []
    },

    hasRefunds() {
      return this.refundsList.length > 0
    },

    refundSummary() {
      const s = this.orderItem.refund_summary
      if (s) {
        return {
          total_refundable: parseFloat(s.total_refundable) || 0,
          total_refunded: parseFloat(s.total_refunded) || 0,
          available: parseFloat(s.available) || 0
        }
      }

      return {
        total_refundable: 0,
        total_refunded: 0,
        available: 0
      }
    },

    canCreateRefund() {
      return this.refundSummary.available > 0
    },

    refundAmountNum() {
      return parseFloat(this.refundAmount) || 0
    },

    amountError() {
      return this.refundAmountNum > this.refundSummary.available
    },

    refundedPercentage() {
      if (this.refundSummary.total_refundable === 0) return '0'
      return ((this.refundSummary.total_refunded / this.refundSummary.total_refundable) * 100).toFixed(0)
    },

    currentPercentage() {
      if (this.refundSummary.total_refundable === 0) return '0'
      return ((this.refundAmountNum / this.refundSummary.total_refundable) * 100).toFixed(0)
    },

    percentageColor() {
      const pct = parseFloat(this.currentPercentage)
      if (pct >= 100) return 'danger'
      if (pct >= 50) return 'warning'
      return 'info'
    },

    hasPhotoSelections() {
      return this.removedPhotos.length > 0
    },

    isFullRemoval() {
      return this.purchasedCount > 0 && this.removedPhotos.length >= this.purchasedCount
    },

    showRecalculateButton() {
      return this.removedPhotos.length > 0 && this.refundAmountNum > 0 && this.calculatedFromPhotos
    },

    canSubmit() {
      const hasAmount = this.refundAmountNum > 0 && !this.amountError
      return (hasAmount || this.hasPhotoSelections) && !this.creatingRefund && !this.calculatingRefund
    },

    refundTypeOptions() {
      return this.refundTypes.map(rt => ({value: rt.id, label: rt.label}))
    }
  },
  async mounted() {
    await this.fetchRefundTypes()
    if (this.refundTypeOptions.length > 0 && !this.selectedRefundType) {
      this.selectedRefundType = this.refundTypeOptions[0].value
    }
  },
  watch: {
    removedPhotos: {
      handler(newVal) {
        if (newVal.length > 0) {
          this.calculateRefund()
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('core', ['fetchRefundTypes']),

    createRefund(data) {
      return this.$store.dispatch(`${this.storeNamespace}/createRefund`, data)
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      return num.toFixed(2)
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    getRefundTypeLabel(type) {
      return type?.name || '-'
    },

    getSelectedRefundTypeName() {
      const rt = this.refundTypes.find(rt => rt.id === this.selectedRefundType)
      return rt ? rt.label : '-'
    },

    getRefundRoute(refundId) {
      const params = { id: refundId }
      const isMovement = this.storeNamespace === 'accounting/movement'

      if (this.scope.type === 'contract-manager') {
        params.contractManagerId = this.scope.id
        return { name: isMovement ? 'contractManager.movements.refund' : 'contractManager.accounting.refund', params }
      } else if (this.scope.type === 'pica-service') {
        params.picaServiceId = this.scope.id
        return { name: isMovement ? 'event.movements.refund' : 'event.accounting.refund', params }
      }
      return { name: 'admin.accounting.refund', params }
    },

    async calculateRefund() {
      if (this.removedPhotos.length === 0) return

      if (this.isFullRemoval) {
        this.refundAmount = this.refundSummary.available.toFixed(2)
        this.calculatedFromPhotos = true
        return
      }

      this.calculatingRefund = true
      try {
        const removedMediaIds = this.removedPhotos.map(p => p.id)
        const result = await AccountingService.calculateRefundAmount(
          this.scope,
          this.orderItem.id,
          removedMediaIds
        )
        this.refundAmount = parseFloat(result.refund_amount).toFixed(2)
        this.calculatedFromPhotos = true
      } catch (e) {
        // Fallback silenzioso: l'utente potrà inserire l'importo manualmente
      } finally {
        this.calculatingRefund = false
      }
    },

    fillMax() {
      this.refundAmount = this.refundSummary.available.toFixed(2)
      this.$emit('select-all-removed')
    },

    clearAll() {
      this.refundAmount = ''
      this.refundReason = ''
      this.calculatedFromPhotos = false
      this.$emit('clear-photos')
    },

    openConfirmModal() {
      if (!this.canSubmit) return
      this.showConfirmModal = true
    },

    async submitRefund() {
      if (this.creatingRefund) return

      const data = {
        order_id: this.orderItem.order?.id || this.orderItem.id,
        order_item_id: this.orderItem.id,
        amount: this.refundAmountNum.toFixed(2),
        refund_type: this.selectedRefundType,
        reason: this.refundReason.trim()
      }

      if (this.removedPhotos.length > 0) {
        data.removed_media_ids = this.removedPhotos.map(p => p.id)
      }

      const success = await this.createRefund(data)
      if (success) {
        this.showConfirmModal = false
        this.refundAmount = ''
        this.refundReason = ''
        this.calculatedFromPhotos = false
        this.$emit('clear-photos')
      }
    }
  }
}
</script>

<style scoped>
.refund-box {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: white;
  padding: 0 16px;
}

.refund-section {
  padding: 16px 0;
}

.refund-section + .refund-section {
  border-top: 1px solid #e4e7ea;
}

.refund-section__header {
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  color: #333;
}

.refund-input-row {
  display: flex;
  align-items: flex-end;
}

.refund-amount-input {
  max-width: 160px;
}

.refund-amount-input >>> input {
  text-align: right;
  font-size: 1.1rem;
  font-weight: 600;
}

.refund-pct {
  font-size: 0.85rem;
  padding: 4px 10px;
}

.history-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.history-item:last-child {
  border-bottom: none;
}

.reference-link {
  color: #e67e22;
  text-decoration: underline;
  font-weight: 500;
}

.reference-link:hover {
  color: #d35400;
}

.confirm-content {
  display: flex;
  flex-direction: column;
}

.confirm-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.confirm-row:last-child {
  border-bottom: none;
}

@media (max-width: 768px) {
  .refund-input-row {
    flex-direction: column;
    align-items: stretch;
  }

  .refund-percentage-badge {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}
</style>
